import { useEffect, useState } from "react";
import { SnapButton } from "../suit";
import { bankActions } from "../types/banking";
import { Divider } from "../components";
import useModal from "../hooks/use-modal";
import TransferModal from "./transfer";
import {
  SpendDebitCardPlus,
  useSpendOrganizationAccountsQuery,
  useSpendOrganizationAllActiveCustomerCardsQuery,
} from "../graphql/generated";
import { FormatMoney } from "../helpers/format-money";
import useOrganization from "../hooks/use-organization";
import useGroup from "../hooks/use-group";
import OrganizationContext from "../context/organization-context";
import GroupContext from "../context/group-context";
import ToastContext from "context/toast-context";
import useToast from "hooks/use-toast";
import GlobalToastMessage from "components/global-toast-message";
import ScreenSizeContext from "context/screen-size-context";
import { useScreenSize } from "hooks/use-screen-size";
import DebitCards from "./debit-cards";
import SendAch from "./send-ach";
import SendCheck from "./send-check";
import { APP_ENVS, TIER1, TIERS } from "../constants";
import useEnvironment from "hooks/use-environment";
import EnvironmentContext from "context/environment-context";
import TierOneDebitCard from "./tier-one-debit-card";

type SpendLiteWidgetProps = {
  environment: (typeof APP_ENVS)[number];
  tier: (typeof TIERS)[number];
  isAddDebitCardsEnabled: boolean;
};

const SpendLiteWidget = ({
  environment,
  tier,
  isAddDebitCardsEnabled,
}: SpendLiteWidgetProps) => {
  const screenSize = useScreenSize();
  const toast = useToast();
  const env = useEnvironment({ envType: environment });
  const organization = useOrganization();
  const group = useGroup();
  const { isOpen: isTransferOpen, toggle: toggleTransfer } = useModal();
  const {
    isOpen: isTierOneDebitCardModalOpen,
    toggle: toggleTierOneDebitCard,
  } = useModal();
  const { isOpen: isDebitCardsOpen, toggle: toggleDebitCards } = useModal();
  const { isOpen: isSendAchOpen, toggle: toggleSendAch } = useModal();
  const { isOpen: isSendCheckOpen, toggle: toggleSendCheck } = useModal();
  const [totalBalance, setTotalBalance] = useState(FormatMoney(0));
  const bankingActions: bankActions[] = [
    {
      icon: "cash-solid",
      label: "Deposit",
      onClick: () => {},
      className: "mt-2 lg:hidden",
    },
    {
      icon: "switch-horizontal-solid",
      label: "Transfer",
      onClick: toggleTransfer,
      className: "mt-4",
    },
    {
      icon: "cash-solid",
      label: "Send Ach",
      onClick: toggleSendAch,
      className: "mt-4",
    },
    {
      icon: "mail-solid",
      label: "Send Check",
      onClick: toggleSendCheck,
      className: "mt-4",
    },
    {
      icon: "credit-card-solid",
      label: "Debit Cards",
      onClick: toggleDebitCards,
      className: "mt-4",
    },
  ];
  const [actions, setActions] = useState<bankActions[]>([]);
  const [cards, setCards] = useState<SpendDebitCardPlus[]>([]);

  const { loading: loadingAccounts, data: accountData } =
    useSpendOrganizationAccountsQuery();

  const {
    loading: loadingCards,
    data: cardData,
    refetch: refetchCards,
  } = useSpendOrganizationAllActiveCustomerCardsQuery();

  useEffect(() => {
    if (!loadingCards && cardData) {
      const cards = cardData.spendOrganizationAllActiveCustomerCards
        ?.cards as SpendDebitCardPlus[];
      setCards(cards);
    }
  }, [loadingCards, cardData, isAddDebitCardsEnabled]);

  useEffect(() => {
    if (
      !loadingAccounts &&
      accountData &&
      accountData.spendOrganizationAccounts
    ) {
      let spendAccounts = accountData.spendOrganizationAccounts;
      setTotalBalance(FormatMoney(spendAccounts.totalBalance ?? 0));
    }
    // eslint-disable-next-line
  }, [loadingAccounts, accountData]);

  useEffect(() => {
    if (tier === TIER1) {
      bankingActions.splice(4, 1, {
        ...bankingActions[4],
        onClick: toggleTierOneDebitCard,
        label: "Order Debit Card",
      });
      setActions(
        bankingActions.filter((ba) => !["Transfer"].includes(ba.label))
      );
    } else if (!isAddDebitCardsEnabled && !cards.length) {
      setActions(bankingActions.filter((ba) => ba.label !== "Debit Cards"));
    } else if (isAddDebitCardsEnabled && !cards.length) {
      bankingActions.splice(4, 1, {
        ...bankingActions[4],
        label: "Order Debit Card",
      });
    } else {
      setActions(bankingActions);
    }
  }, [tier, cards.length, isAddDebitCardsEnabled]);

  return (
    <EnvironmentContext.Provider value={env}>
      <ScreenSizeContext.Provider value={screenSize}>
        <ToastContext.Provider value={toast}>
          <OrganizationContext.Provider value={organization}>
            <GroupContext.Provider value={group}>
              {toast?.isToastOpen && (
                <GlobalToastMessage
                  title={toast.title}
                  message={toast?.message}
                  isToastOpen={toast?.isToastOpen}
                  toggleToast={toast?.toggleToast}
                  type={toast?.type}
                  className={"mt-2"}
                />
              )}
              <h1 className="text-lg font-bold">
                Total Available Balance (as of today)
              </h1>
              <Divider className="w-[80%]" />
              <div className="flex">
                <h1 className="text-4xl font-bold mt-5">
                  {totalBalance.split(".")[0]}
                </h1>
                <h1 className="text-2xl font-bold mt-5 self-end">
                  .{totalBalance.split(".")[1]}
                </h1>
              </div>
              <div className="lg:flex flex-col hidden max-w-xs mt-5">
                {actions.map((actionItem, idx) => {
                  return (
                    <SnapButton
                      key={actionItem.label + idx}
                      fullWidth
                      icon={actionItem.icon}
                      onClick={actionItem.onClick}
                      className={actionItem.className}
                      variant="secondary"
                      size="md"
                    >
                      {actionItem.label}
                    </SnapButton>
                  );
                })}
              </div>
              {isTransferOpen && (
                <TransferModal
                  isOpen={isTransferOpen}
                  toggle={toggleTransfer}
                />
              )}
              {isTierOneDebitCardModalOpen && (
                <TierOneDebitCard
                  isOpen={isTierOneDebitCardModalOpen}
                  toggle={toggleTierOneDebitCard}
                />
              )}
              {isDebitCardsOpen && (
                <DebitCards
                  isOpen={isDebitCardsOpen}
                  toggle={toggleDebitCards}
                  isAddDebitCardsEnabled={isAddDebitCardsEnabled}
                  cards={cards}
                  refetchCards={refetchCards}
                />
              )}
              {isSendAchOpen && (
                <SendAch isOpen={isSendAchOpen} toggle={toggleSendAch} />
              )}
              {isSendCheckOpen && (
                <SendCheck isOpen={isSendCheckOpen} toggle={toggleSendCheck} />
              )}
            </GroupContext.Provider>
          </OrganizationContext.Provider>
        </ToastContext.Provider>
      </ScreenSizeContext.Provider>
    </EnvironmentContext.Provider>
  );
};

export default SpendLiteWidget;
